import React, { useEffect, useState } from 'react';
import { TextField, Autocomplete, Box, Typography } from '@mui/material';
import { buscarLocalidades } from '../../../services/localidadService';
import { componerDomicilios } from '../../../utils/validacionesCaratula';

/**
 * Controles del modal de carátula que necesitan estado propio (por eso no viven dentro de
 * ControlCaratula, que es una función de render sin hooks). Reciben `propsBase`, las mismas
 * props de TextField que usan los demás renglones, para verse igual en el papel.
 */

const etiquetaLocalidad = (op) => {
    if (!op) return '';
    const provincia = op.provincia?.nombre;
    return provincia ? `${op.localidad} (${provincia})` : (op.localidad || '');
};

/** Localidad del catálogo: autocompletado contra /localidades/buscar (mín. 2 letras). */
function SelectorLocalidad({ datos, onElegir, propsBase }) {
    const [opciones, setOpciones] = useState([]);
    const [texto, setTexto] = useState('');
    const [buscando, setBuscando] = useState(false);

    const valor = datos?.idLocalidad ? { id: datos.idLocalidad, localidad: datos.localidad } : null;

    useEffect(() => {
        const consulta = texto.trim();
        if (consulta.length < 2 || consulta === etiquetaLocalidad(valor)) {
            return undefined;
        }
        let vigente = true;
        const temporizador = setTimeout(async () => {
            setBuscando(true);
            try {
                const resultado = await buscarLocalidades(consulta);
                if (vigente) setOpciones(Array.isArray(resultado) ? resultado.slice(0, 30) : []);
            } catch (err) {
                console.error('❌ Error buscando localidades:', err);
                if (vigente) setOpciones([]);
            } finally {
                if (vigente) setBuscando(false);
            }
        }, 300);
        return () => {
            vigente = false;
            clearTimeout(temporizador);
        };
        // `valor` se deriva de datos en cada render: depender de él relanzaría la búsqueda sin
        // que cambie el texto. Alcanza con el texto tipeado.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [texto]);

    return (
        <Autocomplete
            size="small"
            options={valor && !opciones.some((o) => o.id === valor.id) ? [valor, ...opciones] : opciones}
            value={valor}
            loading={buscando}
            filterOptions={(x) => x}
            getOptionLabel={etiquetaLocalidad}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText={texto.trim().length < 2 ? 'Escribí al menos 2 letras' : 'Sin resultados'}
            loadingText="Buscando..."
            onInputChange={(e, nuevo, motivo) => { if (motivo === 'input') setTexto(nuevo); }}
            onChange={(e, op) => onElegir(op)}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="standard"
                    placeholder="Localidad"
                    title={propsBase.title}
                    InputProps={{ ...params.InputProps, sx: propsBase.InputProps?.sx }}
                />
            )}
        />
    );
}

/**
 * Domicilio en partes: Calle · Nro · Localidad. Cada cambio actualiza el objeto estructurado
 * (form[estructurado], lo que se guarda en el cliente) Y el String del renglón (form[clave],
 * lo que se imprime), compuesto igual que en el backend. El barrio no se edita: se conserva
 * el que ya tenía el domicilio y entra en el renglón compuesto.
 *
 * `campo.estructurado` puede ser una lista (Reajuste Badaro imprime "DNI / REAL" en un solo
 * renglón): se dibuja un grupo por domicilio.
 */
export function ControlDomicilio({ campo, clave, form, onChange, error, propsBase }) {
    const estructurados = [].concat(campo.estructurado || []);
    const etiquetas = campo.etiquetasEstructurado || [];

    const actualizar = (k, cambios) => {
        const nuevo = { ...(form[k] || {}), ...cambios };
        onChange(k, nuevo);
        onChange(clave, componerDomicilios(estructurados.map((kk) => (kk === k ? nuevo : form[kk]))));
    };

    const estiloInput = { ...propsBase.InputProps, disableUnderline: undefined };

    return (
        <Box>
            {estructurados.map((k, i) => {
                const datos = form[k] || {};
                return (
                    <Box key={k} sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap', mb: 0.5 }}>
                        {etiquetas[i] && (
                            <Typography component="span" sx={{ fontSize: '0.75rem', color: '#64748b', minWidth: 36 }}>
                                {etiquetas[i]}
                            </Typography>
                        )}
                        <TextField
                            variant="standard"
                            placeholder="Calle"
                            title={propsBase.title}
                            value={datos.calle ?? ''}
                            onChange={(e) => actualizar(k, { calle: e.target.value })}
                            inputProps={{ maxLength: 100 }}
                            InputProps={estiloInput}
                            sx={{ flex: '3 1 180px' }}
                        />
                        <TextField
                            variant="standard"
                            placeholder="Nro"
                            title={propsBase.title}
                            value={datos.altura ?? ''}
                            onChange={(e) => actualizar(k, { altura: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                            inputProps={{ inputMode: 'numeric' }}
                            InputProps={estiloInput}
                            sx={{ flex: '0 1 70px' }}
                        />
                        <Box sx={{ flex: '2 1 180px' }}>
                            <SelectorLocalidad
                                datos={datos}
                                propsBase={propsBase}
                                onElegir={(op) => actualizar(k, op
                                    ? { idLocalidad: op.id, localidad: op.localidad }
                                    : { idLocalidad: null, localidad: '' })}
                            />
                        </Box>
                    </Box>
                );
            })}
            {error && (
                <Typography sx={{ fontSize: '0.75rem', color: '#d32f2f' }}>{error}</Typography>
            )}
        </Box>
    );
}
